import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import BorderGlow from "../components/BorderGlow";
import { API_BASE } from "../config/api";
import "./login.css";

const Login = ({ isAdmin = false }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectAfterLogin = (nextRole) => {
    const target = nextRole === "admin" ? "/admin/dashboard" : "/home";
    // Hard navigation avoids occasional stale-router state after OAuth callbacks.
    window.location.replace(target);
  };

  useEffect(() => {
    const existingToken = localStorage.getItem("token");
    const existingRole = localStorage.getItem("role");

    if (existingToken) {
      navigate(existingRole === "admin" ? "/admin/dashboard" : "/home", {
        replace: true,
      });
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = {};

      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        console.error("Non-JSON response received from server:", text);
        alert(`Server error (${res.status}): Server returned unexpected content. Verify API URL: ${API_BASE}`);
        return;
      }

      if (!res.ok) {
        alert(data.message || "Login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role);
      window.dispatchEvent(new Event("authChanged"));
      redirectAfterLogin(data.role);
    } catch (err) {
      console.error("Login request error:", err);
      alert(`Unable to connect to server (${API_BASE}). Please check your internet connection or try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      alert("Google credential missing");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = {};

      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        console.error("Non-JSON response received from Google auth:", text);
        alert(`Server error (${res.status}): Server returned unexpected content.`);
        return;
      }

      if (!res.ok) {
        alert(data.message || "Google login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role || "user");
      window.dispatchEvent(new Event("authChanged"));
      redirectAfterLogin(data.role || "user");
    } catch (err) {
      console.error("Google login request error:", err);
      alert(`Unable to connect to server (${API_BASE}).`);
    }
  };

  return (
      <div className="login-page">
        {/* Home button just left of the card */}
        {/* Home button just left of the card, visible and vertically centered */}
        <button
          className="home-nav-btn"
          type="button"
          onClick={() => navigate("/home")}
          aria-label="Go to Home"
        >
          Home
        </button>
        <BorderGlow
          className="login-card-glow"
          backgroundColor="#1e293b"
          borderRadius={16}
          glowRadius={35}
          glowColor="240 80 80"
          colors={['#60a5fa', '#ec4899', '#8b5cf6']}
        >
          <div className="login-card-inner">
            {/* ...existing code... */}
            <div className="login-header">
              <img src="/images/logo.png" alt="YUVA" />
              <h2>{isAdmin ? "Admin sign in" : "Welcome back"}</h2>
              <p>{isAdmin ? "Sign in to continue to YUVA Admin" : "Sign in to continue to YUVA"}</p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            {!isAdmin && (
              <>
                <div className="login-divider">OR</div>

                <div className="google-login">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => alert("Google Login Failed")}
                  />
                </div>

                <p className="signup-text">
                  Don't have an account?{" "}
                  <Link to="/signup">Create one</Link>
                </p>
              </>
            )}
          </div>
        </BorderGlow>
      </div>
  );
};
export default Login;
